import { Card, CardContent, Grid, Stack, Typography } from '@mui/material';

import { useCallback, useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CancelOutlineIcon } from 'common/assets/images/cancel-outline.svg';
import { ReactComponent as NoActivityIcon } from 'common/assets/images/NoActivity.svg';
import { ReactComponent as RegenerateOutlinIcon } from 'common/assets/images/regenerate-outline.svg';
import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import SearchSelectDropdownEle from 'common/components/SearchableSelectDropdown/SearchSelectDropdown';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	CONTROL_TESTSCRIPT_STEPS,
	CONTROL_TESTSCRIPT_ACTIONS_DESCRIPTION,
	CONTROL_TESTSCRIPT_ACTIONS_TYPE,
	CONTROL_TESTSCRIPT_ACTIONS_MODAL_TITLE,
} from 'common/constants/TestScriptHubControlConstants';
import { TestScriptHubText } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setControlStepsInfo,
	setControlTestClicked,
} from 'common/store/reducer/controlTestScriptReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import {
	getGenAITestScripts,
	getTestSelectionControlDetails,
	getTestSelectionTestAreaDetails,
	getTestSelectionTestPlanDetails,
} from 'features/TestMonitoring/api';
import { ITestSelectControlData } from 'features/TestMonitoring/api/type-def';

import ControlTestScriptStepsLayout from '../ControlTestSteps/ControlTestScriptStepsLayout';
import ControlTestStepActions from '../ControlTestSteps/ControlTestStepActions/ControlTestStepActions';

import PageTitle from '../../../../components/PageTitle/PageTitle';

import ControlDescription from './ControlDescription/ControlDescription';
import ExistingTestScripts from './ExistingTestScripts/ExistingTestScripts';
import TestAreaDetails from './TestAreaDetails/TestAreaDetails';

import styles from './ControlTestSelect.module.scss';

interface Option {
	id: string;
	name: string;
}
interface IControlTestSelectProps {
	secondLevelBreadcrumbs: React.ReactNode;
}

const { OK } = HTTP_STATUS_CODES;

const {
	GENERATE_TESTSCRIPT,
	MANUALLY_GENERATE_TESTSCRIPT,
	EXISTING_GENERATE_TESTSCRIPT,
} = CONTROL_TESTSCRIPT_STEPS;
const { REGENERATE_TEST_SCRIPT, CANCEL_TEST_SCRIPT } =
	CONTROL_TESTSCRIPT_ACTIONS_DESCRIPTION;
const { REGENERATE_TEST_ACTION, CANCEL_TEST_ACTION } =
	CONTROL_TESTSCRIPT_ACTIONS_TYPE;
const { REGENERATE_TEST_SCRIPT_MODAL_TITLE, CANCEL_TEST_SCRIPT_MODAL_TITLE } =
	CONTROL_TESTSCRIPT_ACTIONS_MODAL_TITLE;

const ControlTestSelectionLandingPage = ({
	secondLevelBreadcrumbs,
}: IControlTestSelectProps) => {
	const dispatch = useDispatch();
	const [testPlanSelectOptions, setTestPlanSelectOptions] = useState<Option[]>(
		[],
	);
	const [testPlanOptions, setTestPlanOptions] = useState<Option[]>([]);
	const [testPlanId, setTestPlanId] = useState<string>('');
	const [testPlanSelectedValue, setTestPlanSelectedValue] = useState('');
	const [testPlanSearchText, setTestPlanSearchText] = useState('');
	const [testAreaId, setTestAreaId] = useState<string>('');
	const [testAreaSelectOptions, setTestAreaSelectOptions] = useState<Option[]>(
		[],
	);
	const [testAreaOptions, setTestAreaOptions] = useState<Option[]>([]);
	const [testAreaSelectedValue, setTestAreaSelectedValue] = useState('');
	const [testAreaSearchText, setTestAreaSearchText] = useState('');
	const [testAreaDropdownDisabled, setTestAreaDropdownDisabled] =
		useState<boolean>(true);
	const [controlSelectOptions, setControlSelectOptions] = useState<Option[]>(
		[],
	);
	const [controlOptions, setControlOptions] = useState<Option[]>([]);
	const [controlId, setControlId] = useState<string>('');
	const [controlSelectedValue, setControlSelectedValue] = useState<string>('');
	const [controlSearchText, setControlSearchText] = useState('');
	const [enableStepsLayout, setTestStepsLayout] = useState<boolean>(false);
	const [controlDropdownDisabled, setControlDropdownDisabled] =
		useState<boolean>(true);
	const [controlData, setControlData] = useState<ITestSelectControlData>({
		citations: [],
		risks: [],
		processes: [],
		policies: [],
		products: [],
		controls: [],
		termsAndConditions: [],
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [testStepslayoutType, setTestStepsLayoutType] = useState<string>('');
	const [actionsType, setActionsType] = useState<string>('');
	const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(true);
	const [generateTestscriptBtn, setGenerateTestscriptBtn] =
		useState<boolean>(true);
	const [openRegenerateModal, setReGenerateTestscriptModal] =
		useState<boolean>(false);
	const [alertText, setAlertText] = useState<string>('');
	const [modalTitle, setModalTitle] = useState<string>('');
	const [generatedSteps, setGeneratedSteps] = useState<string[]>([]);
	const [isDataLoading, setDataLoading] = useState<any>({
		genAIDataLoading: true,
	});
	const [cancelClicked, setCancelClicked] = useState<boolean>(false);

	const fetchTestSelectTestPlanDetails = async () => {
		const resp: any = await getTestSelectionTestPlanDetails();
		if (resp?.statusCode === OK) {
			const mappedTestPlanOptions = resp?.responseBody.map((plan: any) => ({
				id: plan.id,
				name: plan.testPlanName,
			}));
			setTestPlanOptions(mappedTestPlanOptions);
			setLoading(false);
		} else {
			setControlData({
				citations: [],
				risks: [],
				processes: [],
				policies: [],
				products: [],
				controls: [],
				termsAndConditions: [],
			});
		}
	};

	const fetchTestSelectTestAreaDetails = async () => {
		if (testPlanId) {
			const resp: any = await getTestSelectionTestAreaDetails(testPlanId);
			if (resp?.statusCode === OK) {
				const mappedTestAreaOptions = resp?.responseBody.map((area: any) => ({
					id: area.id,
					name: area.testAreaName,
				}));
				setTestAreaOptions(mappedTestAreaOptions);
				setLoading(false);
			} else {
				setControlData({
					citations: [],
					risks: [],
					processes: [],
					policies: [],
					products: [],
					controls: [],
					termsAndConditions: [],
				});
			}
		}
	};
	const fetchTestSelectControlDetails = async () => {
		if (testAreaId) {
			const resp: any = await getTestSelectionControlDetails(testAreaId);
			if (resp?.statusCode === OK) {
				setControlData(resp?.responseBody);
				const mappedControlOptions = resp?.responseBody.controls.map(
					(control: any) => ({
						id: control.controlId,
						name: control.controlName,
					}),
				);
				setControlOptions(mappedControlOptions);
				setLoading(false);
			} else {
				setControlData({
					citations: [],
					risks: [],
					processes: [],
					policies: [],
					products: [],
					controls: [],
					termsAndConditions: [],
				});
			}
		}
	};

	useEffect(() => {
		fetchTestSelectTestPlanDetails();
	}, []);

	useEffect(() => {
		fetchTestSelectTestAreaDetails();
	}, [testPlanId]);

	useEffect(() => {
		fetchTestSelectControlDetails();
	}, [testAreaId]);

	// search functionality for plan dropdown
	useEffect(() => {
		if (testPlanSearchText.trim() !== '') {
			const filteredOptionsVal = testPlanOptions
				.filter((option) => option.name !== null)
				.filter((option) =>
					option.name.toLowerCase().includes(testPlanSearchText.toLowerCase()),
				);
			setTestPlanSelectOptions(filteredOptionsVal);
		} else {
			setTestPlanSelectOptions(testPlanOptions);
		}
	}, [testPlanSearchText, testPlanOptions]);

	// to fetch the selected plan id
	useEffect(() => {
		const selectedOption = testPlanOptions.find(
			(option) => option.name === testPlanSelectedValue,
		);
		if (selectedOption) {
			setTestPlanId(selectedOption.id);
		}
	}, [testPlanSelectedValue, testPlanOptions]);

	// search functionality for area dropdown
	useEffect(() => {
		if (testAreaSearchText.trim() !== '') {
			const filteredOptionsVal = testAreaOptions
				.filter((option) => option.name !== null)
				.filter((option) =>
					option.name.toLowerCase().includes(testAreaSearchText.toLowerCase()),
				);
			setTestAreaSelectOptions(filteredOptionsVal);
		} else {
			setTestAreaSelectOptions(testAreaOptions);
		}
	}, [testAreaSearchText, testAreaOptions]);

	// to fetch the selected area id
	useEffect(() => {
		const selectedOption = testAreaOptions.find(
			(option) => option.name === testAreaSelectedValue,
		);
		if (selectedOption) {
			setTestAreaId(selectedOption.id);
		}
	}, [testAreaSelectedValue, testAreaOptions]);

	// search functionality for control dropdown
	useEffect(() => {
		if (controlSearchText.trim() !== '') {
			const filteredOptionsVal = controlOptions
				.filter((option) => option.name !== null)
				.filter((option) =>
					option.name.toLowerCase().includes(controlSearchText.toLowerCase()),
				);
			setControlSelectOptions(filteredOptionsVal);
		} else {
			setControlSelectOptions(controlOptions);
		}
	}, [controlSearchText, controlOptions]);

	// to fetche selected control id
	useEffect(() => {
		const selectedOption = controlOptions.find(
			(option) => option.name === controlSelectedValue,
		);
		if (selectedOption) {
			setControlId(selectedOption.id);
		}
	}, [controlSelectedValue, controlOptions]);

	const handleTestPlanSelectChange = useCallback(
		(event: React.ChangeEvent<{ value: string }>) => {
			// to reset the values when the plan is changes
			if (event.target.value !== testPlanSelectedValue) {
				setTestPlanSelectedValue(event.target.value);
				setControlDropdownDisabled(true);
				setTestAreaSelectedValue('');
				setControlOptions([]);
				setControlSelectedValue('');
				setButtonsDisabled(true);
				setTestAreaDropdownDisabled(false);
				onResetStepsLayout();
				setTestAreaSearchText('');
				setControlSearchText('');
				setTestPlanSearchText('');
			}
		},
		[testPlanSelectedValue],
	);
	const handleTestAreaSelectChange = useCallback(
		(event: React.ChangeEvent<{ value: string }>) => {
			// reset the values of control when the area is change
			if (event.target.value !== testAreaSelectedValue) {
				setTestAreaSelectedValue(event.target.value);
				setControlSelectedValue('');
				setTestAreaSearchText('');
				setControlOptions([]);
				setControlDropdownDisabled(false);
				setButtonsDisabled(true);
				onResetStepsLayout();
				setControlSearchText('');
				setTestPlanSearchText('');
			}
		},
		[testAreaSelectedValue],
	);

	const handleControlSelectChange = useCallback(
		(event: React.ChangeEvent<{ value: string }>) => {
			setControlSelectedValue(event.target.value);
			setButtonsDisabled(false);
			onResetStepsLayout();
			setControlSearchText('');
			setTestPlanSearchText('');
			setTestAreaSearchText('');
		},
		[],
	);

	const handleTestPlanInputChange = (event: any) => {
		setTestPlanSearchText(event.target.value);
	};
	const handleTestAreaInputChange = (event: any) => {
		setTestAreaSearchText(event.target.value);
	};
	const handleControlSearchChange = (event: any) => {
		setControlSearchText(event.target.value);
	};

	const handleGenerateTestScript = (type: any) => {
		dispatch(setControlStepsInfo(false));
		setTestStepsLayoutType(type);
		setGenerateTestscriptBtn(false);
		setAlertText(REGENERATE_TEST_SCRIPT);
	};

	const getGenApiData = async () => {
		setTestStepsLayout(true);
		setLoadingInfo('genAIDataLoading', true);
		const response = await getGenAITestScripts(controlId);
		if (response?.statusCode === OK) {
			const fullStringValue = response.responseBody.testScript;
			const secondStringData = fullStringValue.split('Test Script: ')[1];
			const stringData = secondStringData.substring(
				0,
				secondStringData.length - 1,
			);

			let i = 1;
			const result = [];
			while (i <= stringData.length - 1) {
				const newStr = stringData.indexOf(`${i}.`);
				const newStr2 = stringData.indexOf(`${i + 1}.`);

				if (newStr === -1) {
					break;
				} else {
					if (newStr2 !== -1) {
						const strn = stringData.substring(newStr + 2, newStr2);
						result.push(strn.trim());
					} else {
						const strn = stringData.substring(
							newStr + 2,
							stringData.length - 1,
						);
						result.push(strn.trim());
					}
					i += 1;
				}
			}
			setGeneratedSteps([...result]);
			handleGenerateTestScript(GENERATE_TESTSCRIPT);
			setLoadingInfo('genAIDataLoading', false);
		} else {
			setLoadingInfo('genAIDataLoading', false);
			emptyGenAIdata();
		}
	};

	const emptyGenAIdata = () => {
		setGeneratedSteps([]);
		dispatch(setControlStepsInfo(false));
	};
	const setLoadingInfo = (type: string, val: boolean) => {
		setDataLoading((previous: any) => ({ ...previous, [type]: val }));
	};
	const handleClearText = () => {
		setControlSearchText('');
		setTestAreaSearchText('');
		setTestPlanSearchText('');
	};

	const [modal, setModal] = useState<boolean>(false);

	const onExistingTestScript = useCallback(() => {
		setModal(true);
	}, []);
	const onModalClose = () => {
		setModal(false);
	};
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleClose = useCallback(() => {
		setToastMessage({ isToastOpen: false, severity: '', message: '' });
	}, []);

	const handleReGenerateTestScript = () => {
		setReGenerateTestscriptModal(true);
		setActionsType(REGENERATE_TEST_ACTION);
		setAlertText(REGENERATE_TEST_SCRIPT);
		setModalTitle(REGENERATE_TEST_SCRIPT_MODAL_TITLE);
	};

	const handleManuallyGenerateTestScript = () => {
		setTestStepsLayoutType(MANUALLY_GENERATE_TESTSCRIPT);
		setLayoutTile();
	};

	const setLayoutTile = () => {
		emptyGenAIdata();
		resetLoading();
		setTestStepsLayout(true);
		setGenerateTestscriptBtn(false);
	};
	const resetLoading = () => {
		setLoadingInfo('genAIDataLoading', false);
	};
	const handleActionsModalClose = () => {
		setReGenerateTestscriptModal(false);
	};

	const callGenerateTestScriptHandler = () => {
		if (generateTestscriptBtn) {
			dispatch(setControlStepsInfo(false));
			getGenApiData();
		} else {
			handleReGenerateTestScript();
		}
	};

	const callTestScriptCancelHandler = () => {
		dispatch(setControlStepsInfo(false));
		setReGenerateTestscriptModal(true);
		setGenerateTestscriptBtn(true);
		setAlertText(CANCEL_TEST_SCRIPT);
		setModalTitle(CANCEL_TEST_SCRIPT_MODAL_TITLE);
		setActionsType(CANCEL_TEST_ACTION);
		if (testPlanId === '') {
			setCancelClicked(true);
		}
	};

	const callOnActionSelect = () => {
		setReGenerateTestscriptModal(false);
		if (actionsType === REGENERATE_TEST_ACTION) {
			setGenerateTestscriptBtn(false);
			setTestStepsLayoutType(GENERATE_TESTSCRIPT);
			getGenApiData();
		} else {
			onResetStepsLayout();
			setControlOptions([]);
			onResetControlDropdown();
		}
	};
	const onResetStepsLayout = () => {
		setGenerateTestscriptBtn(true);
		emptyGenAIdata();
		setAlertText('');
		setTestStepsLayout(false);
		dispatch(setControlStepsInfo(false));
	};
	const onResetControlDropdown = () => {
		setTestPlanId('');
		setTestAreaId('');
		setTestAreaSelectedValue('');
		setControlSelectedValue('');
		setTestPlanSelectedValue('');
		setButtonsDisabled(true);
		setTestAreaDropdownDisabled(true);
		setControlOptions([]);
		setControlDropdownDisabled(true);
		setButtonsDisabled(true);
	};
	const navigateToSelection = () => {
		dispatch(setControlTestClicked(false));
	};

	const onSelectTestScript = (data: any) => {
		setLayoutTile();
		setGeneratedSteps([...data]);
		setTestStepsLayoutType(MANUALLY_GENERATE_TESTSCRIPT);
		handleGenerateTestScript(EXISTING_GENERATE_TESTSCRIPT);
		setTestStepsLayout(true);
	};
	useEffect(() => {
		if (testPlanId === '' && cancelClicked) {
			navigateToSelection();
		}
	}, [testPlanId, cancelClicked]);

	const controlDataArray = (controlData: any) => {
		const controlArray = [
			'citations',
			'controls',
			'policies',
			'processes',
			'products',
			'risks',
			'termsAndConditions',
		];

		return controlArray.every((array: any) => controlData[array].length === 0);
	};

	return (
		<Grid xs={12}>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="center"
			/>

			<Grid item>
				<Breadcrumbs breadcrumbs={secondLevelBreadcrumbs} />
				<PageTitle text={TestScriptHubText} />
			</Grid>
			{!loading ? (
				<Grid
					container
					className={`
				${styles['main-content']}
				`}
				>
					<Grid item className={styles['control-test-container']}>
						<Grid container spacing={2}>
							<Card className={styles['card-container']}>
								<CardContent>
									<Typography className={styles['control-test-title']}>
										Control test selection{' '}
									</Typography>
									<Grid container spacing={2}>
										<Grid
											item
											xs={4}
											sm={4}
											md={4}
											lg={4}
											xl={4}
											className={styles['control-test-btn-container']}
										>
											<Stack
												className={`${
													controlDropdownDisabled
														? styles['dropdown-btn-stack']
														: styles['dropdown-btn-stack-data-selected']
												}`}
											>
												<Stack spacing={2}>
													<SearchSelectDropdownEle
														value={testPlanSelectedValue}
														onChange={handleTestPlanSelectChange}
														onInputChange={handleTestPlanInputChange}
														options={testPlanSelectOptions}
														label={toSentenceCase('Test Plan')}
														stylesClassName={styles['control-test-dropdowns']}
														clearable
														clearHandler={handleClearText}
														textValue={testPlanSearchText}
													/>
													<SearchSelectDropdownEle
														value={testAreaSelectedValue}
														onChange={handleTestAreaSelectChange}
														onInputChange={handleTestAreaInputChange}
														options={testAreaSelectOptions}
														label={toSentenceCase('Test Area')}
														stylesClassName={styles['control-test-dropdowns']}
														clearable
														clearHandler={handleClearText}
														textValue={testAreaSearchText}
														disabled={testAreaDropdownDisabled}
													/>
													<SearchSelectDropdownEle
														value={controlSelectedValue}
														onChange={handleControlSelectChange}
														onInputChange={handleControlSearchChange}
														options={controlSelectOptions}
														label="Control"
														stylesClassName={styles['control-test-dropdowns']}
														clearable
														clearHandler={handleClearText}
														textValue={controlSearchText}
														disabled={controlDropdownDisabled}
													/>
												</Stack>
												<Stack spacing={2}>
													{generateTestscriptBtn ? (
														<ButtonComp
															btnType="primary"
															clickHandler={callGenerateTestScriptHandler}
															data-testid="control-test-btn"
															disabled={buttonsDisabled}
														>
															Generate test script
														</ButtonComp>
													) : (
														<ButtonComp
															btnType="primary"
															clickHandler={handleReGenerateTestScript}
															data-testid="control-regeneratetest-btn"
															disabled={buttonsDisabled}
														>
															Regenerate test script
														</ButtonComp>
													)}
													<ButtonComp
														btnType="secondary"
														clickHandler={handleManuallyGenerateTestScript}
														data-testid="control-manualgeneratetest-btn"
														disabled={buttonsDisabled}
													>
														Manually create test script
													</ButtonComp>
													<ButtonComp
														btnType="secondary"
														clickHandler={onExistingTestScript}
														data-testid="obt-test-btn"
														disabled={buttonsDisabled}
													>
														Check for existing test scripts
													</ButtonComp>
													<ButtonComp
														btnType="secondary"
														clickHandler={callTestScriptCancelHandler}
														data-testid="obt-test-cancel-btn"
													>
														Cancel
													</ButtonComp>
												</Stack>
											</Stack>
										</Grid>
										<Grid
											item
											xs={8}
											sm={8}
											md={8}
											lg={8}
											xl={8}
											className={styles['control-test-side-container']}
										>
											{controlDropdownDisabled ||
											controlDataArray(controlData) ? (
												<Stack className={styles['control-test-stack']}>
													<Typography className={styles['no-activity-text']}>
														Please use the dropdowns on the left to make your
														selections.
													</Typography>
													<NoActivityIcon
														className={styles['no-activity-img']}
													/>
												</Stack>
											) : (
												<div>
													{buttonsDisabled ? (
														<TestAreaDetails controlData={controlData} />
													) : (
														<div>
															<ControlDescription
																controlData={controlData}
																selectedOption={controlId}
															/>
															<TestAreaDetails controlData={controlData} />
														</div>
													)}
												</div>
											)}
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>
					</Grid>

					{enableStepsLayout && (
						<Grid container>
							<Grid item className={styles['control-test-generate-scripts']}>
								<Grid container spacing={2}>
									<Card className={styles['card-container']}>
										<CardContent>
											<ControlTestScriptStepsLayout
												type={testStepslayoutType}
												controValue={controlId}
												generatedSteps={generatedSteps}
												loading={isDataLoading.genAIDataLoading}
											/>
										</CardContent>
									</Card>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			) : (
				<Loader show />
			)}
			{openRegenerateModal && testPlanId && (
				<ControlTestStepActions
					openModal={openRegenerateModal}
					title={modalTitle}
					handleClose={handleActionsModalClose}
					handleAction={callOnActionSelect}
					ModalHeaderImg={
						actionsType === REGENERATE_TEST_ACTION
							? RegenerateOutlinIcon
							: CancelOutlineIcon
					}
					type={actionsType}
					alertText={alertText}
					rowIndex={0}
				/>
			)}

			{modal ? (
				<div>
					<ExistingTestScripts
						onClose={onModalClose}
						openModal={modal}
						controlData={controlData}
						selectedOption={controlId}
						onSelectTestScript={onSelectTestScript}
					/>
				</div>
			) : null}
		</Grid>
	);
};

export default ControlTestSelectionLandingPage;
